import { readNewestThreadReplicaCheckpoint } from "@/lib/thread-replica-checkpoint-store"
import { openThreadReplicaDb, requestToPromise, transactionToPromise } from "@/lib/thread-replica-idb"
import {
  FIRST_THREAD_REPLICA_SEQUENCE,
  THREAD_REPLICA_CHECKPOINTS_STORE,
  THREAD_REPLICA_EVENTS_STORE,
  THREAD_REPLICA_META_STORE,
  type ThreadReplicaMetaRecord,
  type ThreadReplicaScope,
  normalizeThreadReplicaMetaRecord,
} from "@/lib/thread-replica-types"
import { buildThreadReplicaEventSequenceRange } from "@/lib/thread-replica-event-store"

const DEFAULT_RUNTIME_CONVERSATION_ID = "default"

async function hasNonDefaultRuntimeConversationRows(args: ThreadReplicaScope & {
  db: IDBDatabase
  fromSequence: number
  toSequence: number
}): Promise<boolean> {
  const transaction = args.db.transaction([THREAD_REPLICA_EVENTS_STORE], "readonly")
  const eventsStore = transaction.objectStore(THREAD_REPLICA_EVENTS_STORE)
  const range = buildThreadReplicaEventSequenceRange({
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    lane: "runtime",
    fromSequence: args.fromSequence,
    toSequence: args.toSequence,
  })

  const hasNonDefault = await new Promise<boolean>((resolve, reject) => {
    const request = eventsStore.openCursor(range)
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"))
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) {
        resolve(false)
        return
      }

      const value = cursor.value as { conversationId?: unknown } | null
      const conversationId = typeof value?.conversationId === "string"
        ? value.conversationId.trim()
        : DEFAULT_RUNTIME_CONVERSATION_ID
      if (conversationId.length > 0 && conversationId !== DEFAULT_RUNTIME_CONVERSATION_ID) {
        resolve(true)
        return
      }
      cursor.continue()
    }
  })
  await transactionToPromise(transaction)
  return hasNonDefault
}

function resolveMaximumRetainedAnchorLag(args: {
  retainRuntimeEventCount: number
}): number {
  // Allow compaction to use a sparse older checkpoint as the prefix anchor and
  // keep up to one extra retained tail window. This avoids forcing exact-anchor
  // checkpoint writes during rapid large-thread switching, while still bounding
  // hydrate replay to roughly twice the configured tail size.
  return args.retainRuntimeEventCount
}

export async function compactThreadReplicaRuntimeTailWindow(args: ThreadReplicaScope & {
  retainRuntimeEventCount: number
  expectedEpoch?: number
  nowMs: number
}): Promise<
  | {
      status: "compacted"
      meta: ThreadReplicaMetaRecord
      checkpointCursor: number
      deletedRuntimeEventCount: number
    }
  | {
      status: "noop"
      reason: "storage_unavailable" | "missing_meta" | "empty_replica" | "already_compacted" | "checkpoint_required" | "multi_conversation_lane"
      meta?: ThreadReplicaMetaRecord
    }
  | {
      status: "stale_writer"
      currentEpoch: number
    }
> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return { status: "noop", reason: "storage_unavailable" }
  }

  const readTransaction = db.transaction([THREAD_REPLICA_META_STORE], "readonly")
  const currentMeta = normalizeThreadReplicaMetaRecord(
    await requestToPromise(
      readTransaction.objectStore(THREAD_REPLICA_META_STORE).get([args.scopeKey, args.sessionId]),
    ),
  )
  await transactionToPromise(readTransaction)

  if (!currentMeta) {
    return { status: "noop", reason: "missing_meta" }
  }

  if (
    typeof args.expectedEpoch === "number" &&
    Number.isFinite(args.expectedEpoch) &&
    Math.floor(args.expectedEpoch) !== currentMeta.replicaEpoch
  ) {
    return { status: "stale_writer", currentEpoch: currentMeta.replicaEpoch }
  }

  if (currentMeta.localCursor < FIRST_THREAD_REPLICA_SEQUENCE) {
    return { status: "noop", reason: "empty_replica", meta: currentMeta }
  }

  const normalizedRetainRuntimeEventCount = Math.max(
    FIRST_THREAD_REPLICA_SEQUENCE,
    Math.floor(args.retainRuntimeEventCount),
  )
  const currentOldestAvailableCursor =
    currentMeta.oldestAvailableCursor >= FIRST_THREAD_REPLICA_SEQUENCE
      ? currentMeta.oldestAvailableCursor
      : FIRST_THREAD_REPLICA_SEQUENCE
  const desiredOldestAvailableCursor = Math.max(
    FIRST_THREAD_REPLICA_SEQUENCE,
    currentMeta.localCursor - normalizedRetainRuntimeEventCount + FIRST_THREAD_REPLICA_SEQUENCE,
  )

  if (desiredOldestAvailableCursor <= currentOldestAvailableCursor) {
    return { status: "noop", reason: "already_compacted", meta: currentMeta }
  }

  if (await hasNonDefaultRuntimeConversationRows({
    ...args,
    db,
    fromSequence: currentOldestAvailableCursor,
    toSequence: currentMeta.localCursor,
  })) {
    return { status: "noop", reason: "multi_conversation_lane", meta: currentMeta }
  }

  const newestCheckpoint = await readNewestThreadReplicaCheckpoint({
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    replicaEpoch: currentMeta.replicaEpoch,
    targetCursor: desiredOldestAvailableCursor - 1,
    eventContractVersion: currentMeta.eventContractVersion,
    projectorVersion: currentMeta.projectorVersion,
  })

  const desiredAnchorCursor = desiredOldestAvailableCursor - 1
  const maximumRetainedAnchorLag = resolveMaximumRetainedAnchorLag({
    retainRuntimeEventCount: normalizedRetainRuntimeEventCount,
  })
  const oldestUsableAnchorCursor = Math.max(
    FIRST_THREAD_REPLICA_SEQUENCE - 1,
    desiredAnchorCursor - maximumRetainedAnchorLag,
  )

  if (!newestCheckpoint || newestCheckpoint.cursor < oldestUsableAnchorCursor) {
    return { status: "noop", reason: "checkpoint_required", meta: currentMeta }
  }

  const nextOldestAvailableCursor = newestCheckpoint.cursor + 1
  if (nextOldestAvailableCursor <= currentMeta.oldestAvailableCursor) {
    return { status: "noop", reason: "already_compacted", meta: currentMeta }
  }

  const transaction = db.transaction(
    [THREAD_REPLICA_META_STORE, THREAD_REPLICA_EVENTS_STORE, THREAD_REPLICA_CHECKPOINTS_STORE],
    "readwrite",
  )
  const metaStore = transaction.objectStore(THREAD_REPLICA_META_STORE)
  const eventsStore = transaction.objectStore(THREAD_REPLICA_EVENTS_STORE)
  const checkpointsStore = transaction.objectStore(THREAD_REPLICA_CHECKPOINTS_STORE)
  const writableMeta = normalizeThreadReplicaMetaRecord(
    await requestToPromise(metaStore.get([args.scopeKey, args.sessionId])),
  )

  if (!writableMeta) {
    transaction.abort()
    return { status: "noop", reason: "missing_meta" }
  }

  if (writableMeta.replicaEpoch !== currentMeta.replicaEpoch) {
    transaction.abort()
    return { status: "stale_writer", currentEpoch: writableMeta.replicaEpoch }
  }

  if (writableMeta.localCursor !== currentMeta.localCursor) {
    transaction.abort()
    return { status: "stale_writer", currentEpoch: writableMeta.replicaEpoch }
  }

  const writableOldestAvailableCursor =
    writableMeta.oldestAvailableCursor >= FIRST_THREAD_REPLICA_SEQUENCE
      ? writableMeta.oldestAvailableCursor
      : FIRST_THREAD_REPLICA_SEQUENCE
  if (nextOldestAvailableCursor <= writableOldestAvailableCursor) {
    transaction.abort()
    return { status: "noop", reason: "already_compacted", meta: writableMeta }
  }

  const deleteRange = buildThreadReplicaEventSequenceRange({
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    lane: "runtime",
    fromSequence: writableOldestAvailableCursor,
    toSequence: nextOldestAvailableCursor - 1,
  })
  const deletedRuntimeEventCount = Math.max(
    0,
    nextOldestAvailableCursor - writableOldestAvailableCursor,
  )

  eventsStore.delete(deleteRange)
  checkpointsStore.delete(
    IDBKeyRange.bound(
      [args.scopeKey, args.sessionId, writableMeta.replicaEpoch, nextOldestAvailableCursor],
      [args.scopeKey, args.sessionId, writableMeta.replicaEpoch, Number.MAX_SAFE_INTEGER],
    ),
  )

  const nextMeta: ThreadReplicaMetaRecord = {
    ...writableMeta,
    oldestAvailableCursor: nextOldestAvailableCursor,
    coverage: "tail_window",
    lastCheckpointCursor: newestCheckpoint.cursor,
    lastUpdatedAtMs: Math.max(0, Math.floor(args.nowMs)),
    lastResetReason: null,
  }

  metaStore.put(nextMeta)
  await transactionToPromise(transaction)
  return {
    status: "compacted",
    meta: nextMeta,
    checkpointCursor: newestCheckpoint.cursor,
    deletedRuntimeEventCount,
  }
}
